import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  GeneratedSubscriptionType,
  useCreateSubscriptionsMutation,
  useUnsubscribeMutation,
} from '../../types/generated';
import { captureError } from '../../utils/capture';
import { ArtistSubscriptionGroup } from './useMyArtistSubscriptions';

export type SubscriptionAlertKind = 'unfollowError' | 'followError';

interface MemberState {
  descriptor: {
    type: GeneratedSubscriptionType;
    objectId: string;
    name: string;
    ref: string;
  };
  artistIds: string[];
  subscriptionId: string;
  subscribed: boolean;
}

export interface ArtistSubscriptionsContextValue {
  alert: SubscriptionAlertKind | null;
  setAlert: (alert: SubscriptionAlertKind | null) => void;
  isFollowed: (artistId: string) => boolean;
  isPending: (artistId: string) => boolean;
  followGroup: (artistId: string) => Promise<void>;
  unfollowGroup: (artistId: string) => Promise<void>;
}

const ArtistSubscriptionsContext =
  createContext<ArtistSubscriptionsContextValue | null>(null);

const memberKey = (type: GeneratedSubscriptionType, objectId: string): string =>
  `${type}:${objectId}`;

export interface ArtistSubscriptionsProviderProps {
  groups: ArtistSubscriptionGroup[];
}

export const ArtistSubscriptionsProvider: React.FC<
  React.PropsWithChildren<ArtistSubscriptionsProviderProps>
> = ({ children, groups }) => {
  const [alert, setAlert] = useState<SubscriptionAlertKind | null>(null);
  const [locallyUnfollowed, setLocallyUnfollowed] = useState<Set<string>>(
    () => new Set(),
  );
  const [pendingArtists, setPendingArtists] = useState<Set<string>>(
    () => new Set(),
  );

  // Per-member state keyed by stable identity. A collab subscription shared by
  // several groups exists once here, so dropping it for one artist and keeping
  // it for another stay in sync. Lives in a ref, the rendered state is driven
  // by `locallyUnfollowed`/`pendingArtists`, this is mutation bookkeeping.
  const stateRef = useRef<{
    members: Map<string, MemberState>;
    groupMembers: Map<string, string[]>;
    knownArtists: Set<string>;
  } | null>(null);

  if (stateRef.current === null) {
    const members = new Map<string, MemberState>();
    const groupMembers = new Map<string, string[]>();
    const knownArtists = new Set<string>();

    groups.forEach((group) => {
      knownArtists.add(group.artist.id);
      groupMembers.set(
        group.artist.id,
        group.members.map((m) => memberKey(m.type, m.objectId)),
      );
      group.members.forEach((m) => {
        const key = memberKey(m.type, m.objectId);
        if (!members.has(key)) {
          members.set(key, {
            descriptor: {
              type: m.type,
              objectId: m.objectId,
              name: m.name,
              ref: m.ref,
            },
            artistIds: m.artistIds,
            subscriptionId: m.subscriptionId,
            subscribed: true,
          });
        }
      });
    });

    stateRef.current = { members, groupMembers, knownArtists };
  }

  const [createMutation] = useCreateSubscriptionsMutation({
    onError: (err) => {
      captureError(err);
      setAlert('followError');
    },
  });

  const [unsubscribeMutation] = useUnsubscribeMutation({
    onError: (err) => {
      captureError(err);
      setAlert('unfollowError');
    },
  });

  const setPending = useCallback((artistId: string, value: boolean) => {
    setPendingArtists((prev) => {
      if (value === prev.has(artistId)) return prev;
      const next = new Set(prev);
      if (value) next.add(artistId);
      else next.delete(artistId);
      return next;
    });
  }, []);

  const markFollowed = useCallback((artistId: string) => {
    setLocallyUnfollowed((prev) => {
      if (!prev.has(artistId)) return prev;
      const next = new Set(prev);
      next.delete(artistId);
      return next;
    });
  }, []);

  const markUnfollowed = useCallback((artistId: string) => {
    setLocallyUnfollowed((prev) => {
      if (prev.has(artistId)) return prev;
      const next = new Set(prev);
      next.add(artistId);
      return next;
    });
  }, []);

  const isFollowed = useCallback(
    (artistId: string) => !locallyUnfollowed.has(artistId),
    [locallyUnfollowed],
  );

  const isPending = useCallback(
    (artistId: string) => pendingArtists.has(artistId),
    [pendingArtists],
  );

  const unfollowGroup = useCallback(
    async (artistId: string) => {
      const { groupMembers, knownArtists, members } = stateRef.current!;
      if (pendingArtists.has(artistId)) return;

      // An artist (other than the one being unfollowed) still keeps a shared
      // subscription alive only if the user follows them now.
      const stillFollowedByOther = (id: string) =>
        id !== artistId && knownArtists.has(id) && !locallyUnfollowed.has(id);

      const toDrop = (groupMembers.get(artistId) ?? [])
        .map((key) => members.get(key))
        .filter((m): m is MemberState => !!m && m.subscribed)
        .filter((m) => {
          const isArtistSub =
            m.descriptor.type === GeneratedSubscriptionType.Artist &&
            m.descriptor.objectId === artistId;
          // The artist sub always goes; a product/release goes only when no
          // other followed artist still references it.
          return isArtistSub || !m.artistIds.some(stillFollowedByOther);
        });

      if (toDrop.length === 0) {
        markUnfollowed(artistId);
        return;
      }

      setPending(artistId, true);
      const result = await unsubscribeMutation({
        variables: { subscriptionIDs: toDrop.map((m) => m.subscriptionId) },
      });
      setPending(artistId, false);

      if (!result.data) return;

      for (const m of toDrop) {
        m.subscribed = false;
      }
      markUnfollowed(artistId);
    },
    [
      locallyUnfollowed,
      markUnfollowed,
      pendingArtists,
      setPending,
      unsubscribeMutation,
    ],
  );

  const followGroup = useCallback(
    async (artistId: string) => {
      const { groupMembers, members } = stateRef.current!;
      if (pendingArtists.has(artistId)) return;

      // Re-create only the snapshot members currently missing, a shared collab
      // sub kept alive by another artist must not be duplicated.
      const toCreate = (groupMembers.get(artistId) ?? [])
        .map((key) => members.get(key))
        .filter((m): m is MemberState => !!m && !m.subscribed);

      if (toCreate.length === 0) {
        markFollowed(artistId);
        return;
      }

      setPending(artistId, true);
      const result = await createMutation({
        variables: { subscriptions: toCreate.map((m) => m.descriptor) },
      });
      setPending(artistId, false);

      if (!result.data) return;

      const freshByKey = new Map(
        result.data.createSubscriptions.subscriptions.map((s) => [
          memberKey(s.type, s.objectId),
          s,
        ]),
      );
      for (const m of toCreate) {
        const fresh = freshByKey.get(
          memberKey(m.descriptor.type, m.descriptor.objectId),
        );
        if (fresh) m.subscriptionId = fresh.id;
        m.subscribed = true;
      }
      markFollowed(artistId);
    },
    [createMutation, markFollowed, pendingArtists, setPending],
  );

  const value = useMemo(
    () => ({
      alert,
      setAlert,
      isFollowed,
      isPending,
      followGroup,
      unfollowGroup,
    }),
    [alert, isFollowed, isPending, followGroup, unfollowGroup],
  );

  return (
    <ArtistSubscriptionsContext.Provider value={value}>
      {children}
    </ArtistSubscriptionsContext.Provider>
  );
};

export const useArtistSubscriptionsContext =
  (): ArtistSubscriptionsContextValue => {
    const ctx = useContext(ArtistSubscriptionsContext);
    if (!ctx) {
      throw new Error(
        'useArtistSubscriptionsContext must be used within an ArtistSubscriptionsProvider',
      );
    }
    return ctx;
  };
